import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Loader2, Eye, EyeOff, KeyRound, ShieldCheck, CheckCircle2, AlertCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import api from '@/lib/api';
import { clearAuth, getAdmin } from '@/lib/auth';
import { toast } from 'sonner';

export default function AdminProfile() {
  const navigate = useNavigate();
  const [admin, setAdmin] = useState(null);
  const [form, setForm] = useState({ current_password: '', new_password: '', confirm_password: '' });
  const [show, setShow] = useState({ current: false, next: false, confirm: false });
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  useEffect(() => { setAdmin(getAdmin()); }, []);

  const validations = {
    minLength: form.new_password.length >= 8,
    hasUpper: /[A-Z]/.test(form.new_password),
    hasLower: /[a-z]/.test(form.new_password),
    hasNumber: /\d/.test(form.new_password),
    different: form.new_password.length > 0 && form.new_password !== form.current_password,
    match: form.new_password.length > 0 && form.new_password === form.confirm_password,
  };

  const allValid = Object.values(validations).every(Boolean) && form.current_password.length > 0;

  const submit = async (e) => {
    e.preventDefault();
    if (!allValid) {
      toast.error('Veuillez corriger les erreurs avant de soumettre.');
      return;
    }
    setLoading(true);
    try {
      await api.post('/admin/change-password', {
        current_password: form.current_password,
        new_password: form.new_password,
      });
      setSuccess(true);
      toast.success('Mot de passe modifié. Vous allez être déconnecté.');
      setTimeout(() => {
        clearAuth();
        navigate('/admin/login');
      }, 2500);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur lors du changement de mot de passe.');
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <div className="max-w-xl mx-auto">
        <div className="card-base p-8 text-center">
          <div className="mx-auto h-16 w-16 rounded-full bg-emerald-100 text-emerald-700 flex items-center justify-center">
            <CheckCircle2 className="h-8 w-8" />
          </div>
          <h1 className="h2 mt-4 text-anthracite-950">Mot de passe modifié</h1>
          <p className="lead mt-2">Votre nouveau mot de passe est désormais actif.</p>
          <p className="mt-3 text-sm text-anthracite-700">Redirection vers la page de connexion…</p>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-2xl">
      <div className="mb-6">
        <h1 className="h2 text-anthracite-950">Mon compte</h1>
        <p className="text-muted-foreground mt-1">Gérez votre profil administrateur</p>
      </div>

      {/* Profile info */}
      <div className="card-base p-6 mb-6">
        <div className="flex items-start gap-4">
          <div className="h-14 w-14 rounded-2xl bg-gradient-to-br from-brand-600 to-brand-800 flex items-center justify-center">
            <ShieldCheck className="h-7 w-7 text-white" />
          </div>
          <div className="flex-1">
            <p className="text-xs uppercase tracking-wider text-muted-foreground">Administrateur connecté</p>
            <p className="font-heading font-semibold text-anthracite-950 text-lg mt-0.5">{admin?.name || 'Administrateur'}</p>
            <p className="text-sm text-anthracite-700 mt-0.5">{admin?.email}</p>
          </div>
        </div>
      </div>

      {/* Change password */}
      <form onSubmit={submit} className="card-base p-6 space-y-5" data-testid="change-password-form">
        <div className="flex items-start gap-3">
          <div className="h-10 w-10 rounded-lg bg-brand-50 text-brand-700 flex items-center justify-center shrink-0">
            <KeyRound className="h-5 w-5" />
          </div>
          <div>
            <h2 className="h3 text-anthracite-950">Changer mon mot de passe</h2>
            <p className="text-sm text-muted-foreground">Choisissez un mot de passe fort que vous ne réutilisez pas ailleurs.</p>
          </div>
        </div>

        <PasswordField
          label="Mot de passe actuel *"
          value={form.current_password}
          onChange={(v) => setForm(f => ({ ...f, current_password: v }))}
          show={show.current}
          onToggle={() => setShow(s => ({ ...s, current: !s.current }))}
          testid="current-password-input"
        />

        <div>
          <PasswordField
            label="Nouveau mot de passe *"
            value={form.new_password}
            onChange={(v) => setForm(f => ({ ...f, new_password: v }))}
            show={show.next}
            onToggle={() => setShow(s => ({ ...s, next: !s.next }))}
            testid="new-password-input"
          />
          {/* Live validation */}
          <ul className="mt-2 grid grid-cols-2 gap-x-3 gap-y-1 text-xs">
            <Rule ok={validations.minLength} label="8 caractères minimum" />
            <Rule ok={validations.hasUpper} label="1 majuscule" />
            <Rule ok={validations.hasLower} label="1 minuscule" />
            <Rule ok={validations.hasNumber} label="1 chiffre" />
            <Rule ok={validations.different} label="Différent de l'ancien" />
          </ul>
        </div>

        <div>
          <PasswordField
            label="Confirmer le nouveau mot de passe *"
            value={form.confirm_password}
            onChange={(v) => setForm(f => ({ ...f, confirm_password: v }))}
            show={show.confirm}
            onToggle={() => setShow(s => ({ ...s, confirm: !s.confirm }))}
            testid="confirm-password-input"
          />
          {form.confirm_password.length > 0 && !validations.match && (
            <p className="mt-1 text-xs text-destructive flex items-center gap-1"><AlertCircle className="h-3 w-3" /> Les mots de passe ne correspondent pas.</p>
          )}
        </div>

        <div className="rounded-lg bg-amber-50 border border-amber-200 p-3 text-xs text-amber-900 flex gap-2">
          <AlertCircle className="h-4 w-4 shrink-0 mt-0.5" />
          <span>Après le changement, vous serez automatiquement déconnecté(e) et devrez vous reconnecter avec le nouveau mot de passe.</span>
        </div>

        <Button type="submit" disabled={loading || !allValid} className="w-full" size="lg" data-testid="change-password-submit">
          {loading ? <><Loader2 className="h-4 w-4 animate-spin" /> Modification…</> : 'Changer mon mot de passe'}
        </Button>
      </form>
    </div>
  );
}

function PasswordField({ label, value, onChange, show, onToggle, testid }) {
  return (
    <div>
      <Label>{label}</Label>
      <div className="relative mt-1.5">
        <Input
          type={show ? 'text' : 'password'}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          required
          className="pr-10"
          data-testid={testid}
          autoComplete={label.includes('actuel') ? 'current-password' : 'new-password'}
        />
        <button
          type="button"
          onClick={onToggle}
          className="absolute right-2 top-1/2 -translate-y-1/2 p-1 rounded text-muted-foreground hover:text-anthracite-900"
          aria-label={show ? 'Masquer le mot de passe' : 'Afficher le mot de passe'}
        >
          {show ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
        </button>
      </div>
    </div>
  );
}

function Rule({ ok, label }) {
  return (
    <li className={`flex items-center gap-1.5 ${ok ? 'text-emerald-700' : 'text-muted-foreground'}`}>
      <span className={`inline-flex h-3.5 w-3.5 items-center justify-center rounded-full ${ok ? 'bg-emerald-100' : 'bg-anthracite-100'}`}>
        {ok ? <CheckCircle2 className="h-3 w-3" /> : <span className="h-1 w-1 rounded-full bg-anthracite-300"></span>}
      </span>
      {label}
    </li>
  );
}
